import { Link } from 'react-router-dom';
import { Movie, categories } from '@/mocks/movies';

interface MovieSectionProps {
  title: string;
  subtitle?: string;
  movies: Movie[];
  viewAllLink?: string;
}

export default function MovieSection({ title, subtitle, movies, viewAllLink }: MovieSectionProps) {
  const getCategoryName = (slug: string) => {
    const cat = categories.find((c) => c.slug === slug);
    return cat ? cat.name : slug;
  };

  return (
    <section className="w-full px-4 md:px-6 py-14">
      <div className="max-w-[1280px] mx-auto">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
              {title}
            </h2>
            {subtitle && (
              <p className="text-sm text-secondary-500 mt-2">{subtitle}</p>
            )}
          </div>
          {viewAllLink && (
            <Link
              to={viewAllLink}
              className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap flex items-center gap-1"
            >
              查看全部 <i className="ri-arrow-right-line text-xs"></i>
            </Link>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
          {movies.map((movie) => (
            <Link
              key={movie.id}
              to={`/category/${movie.category}`}
              className="group block"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              <div className="relative aspect-[2/3] rounded-lg overflow-hidden mb-3 bg-secondary-100">
                <img
                  src={movie.image}
                  alt={movie.title}
                  title={`${movie.title} - 成全影院高清在线观看`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute top-2 right-2 bg-primary-500 text-background-50 text-xs font-bold px-2 py-1 rounded-md whitespace-nowrap">
                  {movie.rating}
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <p className="text-background-50 text-xs leading-relaxed line-clamp-2">{movie.description}</p>
                </div>
              </div>
              <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors truncate">
                {movie.title}
              </h3>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-secondary-400 whitespace-nowrap">{movie.year}</span>
                <span className="text-xs px-2 py-0.5 bg-accent-100 text-accent-700 rounded-full whitespace-nowrap">
                  {getCategoryName(movie.category)}
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}